// =============================================================================
// conversations/startConversationChoice.ts — Lógica pura de decisão da tela
// "Iniciar conversa" (F43-S11, docs/planejamento-inbox-vendedores-waha.md §3.6).
//
// Traduz o contrato de `GET /api/conversations/canais-disponiveis`
// (apps/api/src/modules/conversations/channel-picker.schema.ts) para o que a
// tela precisa mostrar — SEM jargão técnico ("provider", "janela 24h", "WAHA"
// nunca aparecem para o vendedor).
//
// Extraído para função pura (sem React) porque o projeto não usa
// testing-library — toda a decisão "o que mostrar por caixa" precisa ser
// testável isoladamente (DoD do slot).
// =============================================================================

/** Espelha `ChannelWindowSchema` de channel-picker.schema.ts. */
export type ChannelWindow = 'open' | 'human_agent_tag' | 'template_only' | 'closed';

/** Espelha `AvailableChannel` de channel-picker.schema.ts (contrato real do backend). */
export interface AvailableChannelDto {
  readonly channelId: string;
  readonly provider: 'meta_whatsapp' | 'waha';
  readonly rotulo: string;
  readonly janela: ChannelWindow;
  readonly exigeTemplate: boolean;
  readonly sessaoOnline: boolean;
  readonly motivoIndisponivel: string | null;
}

/**
 * Como a caixa deve ser mostrada na tela — o que a decisão de escolha
 * precisa para renderizar cada opção (rádio) sem reimplementar nenhuma regra
 * de negócio, só traduzir o que o backend já decidiu.
 */
export interface ChannelChoiceDisplay {
  readonly channelId: string;
  /** Nome que o vendedor reconhece — nunca o id do canal. */
  readonly label: string;
  /** Frase em português explicando o que essa caixa exige agora. */
  readonly description: string;
  /** true = a caixa não pode ser escolhida agora (mas continua na lista). */
  readonly disabled: boolean;
  /** Presente apenas quando `disabled`. */
  readonly disabledReason: string | null;
  /** true = abre o seletor de template; false = abre o campo de texto livre. */
  readonly requiresTemplate: boolean;
}

/**
 * Frase em português para o que a caixa aceita agora, sem citar o nome
 * técnico do estado (`open`/`template_only`/etc) nem "janela de 24h".
 *
 * A caixa pessoal (WAHA) é sempre `open` (getComposerState já garante isso) —
 * mas a frase não assume provider, só lê o resultado, coerente com a nota do
 * slot: "não reimplemente a regra de janela no front".
 */
function describeWindow(exigeTemplate: boolean): string {
  return exigeTemplate
    ? 'Fora do prazo de resposta livre — precisa de uma mensagem aprovada (template)'
    : 'Conectado — pode escrever livremente';
}

/**
 * Converte uma caixa disponível (contrato do backend) na descrição pronta
 * para a tela. Pura, sem I/O, sem React — testável por unidade.
 */
export function describeChannelChoice(channel: AvailableChannelDto): ChannelChoiceDisplay {
  const disabled = !channel.sessaoOnline;

  return {
    channelId: channel.channelId,
    label: channel.rotulo,
    description: disabled
      ? (channel.motivoIndisponivel ?? 'Esta caixa não está disponível no momento.')
      : describeWindow(channel.exigeTemplate),
    disabled,
    disabledReason: disabled ? (channel.motivoIndisponivel ?? 'Indisponível no momento.') : null,
    // Uma caixa desconectada não tem como saber se vai exigir template quando
    // voltar — não force o seletor de template para uma opção que nem pode
    // ser escolhida.
    requiresTemplate: !disabled && channel.exigeTemplate,
  };
}

/** Converte a lista inteira, preservando a ordem que o backend devolveu. */
export function describeChannelChoices(
  channels: readonly AvailableChannelDto[],
): ChannelChoiceDisplay[] {
  return channels.map(describeChannelChoice);
}

/**
 * A primeira caixa disponível para pré-seleção — nunca uma caixa desabilitada.
 * `null` quando nenhuma caixa está pronta para uso agora (todas desabilitadas
 * ou lista vazia): a tela deve deixar nada pré-selecionado nesse caso.
 */
export function firstAvailableChoice(
  choices: readonly ChannelChoiceDisplay[],
): ChannelChoiceDisplay | null {
  return choices.find((c) => !c.disabled) ?? null;
}
